import React, { useState, useEffect } from 'react';
import { CardPartner } from "../global/CardPartner";
import { SearchForm } from "../global/SearchForm";
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { usePagination, DOTS } from '../global/usePagination';
import { flatListToHierarchical } from "../../components/_hooks/useMenuItems";
import { Button } from "../global/Button";

export const Solutions = (props) => {
  const { t } = useTranslation();

  const fields = props.data;
  const link = props.link;

  let partners = props.items?.edges || [];
  let categories = props.categories?.nodes || [];

  const specialCategories = ["new", "nuevo", "popular", "cexs", "dexs", "on/off ramps", "institutional", "defi", "payments", "bridges", "stablecoins"];
  const specialPartners = ["Money On Chain", "Sovryn", "Tropykus", "BabelFish", "Beefy", "OKU", "Sushi Swap", "Bedrock", "Criptan", "Layerbank", "Stargate", "Alchemy", "Ripio", "Jumper", "OpenOcean", "Merkl",
    "Gamma", "LI.FI", "Boltz", "Orbiter", "Gelato", "Galxe", "Symbiosis", "oooo", "Hyperlane", "Balmy", "Rubic Exchange", "Lemon Cash", "Pagolinea",
    "Andean Wide", "Decrypto",  "SideShift", "iZUMi Finance",  "Steer Finance", "Exodus", "Light Finance", "Wormhole", "Router Protocol", "Buenbit", "LBank", "Rootstock Collective",
    "Mt Pelerin", "Metamask", "Umbrella Network", "MyEtherWallet", "Elk Finance", "NOWnodes", "Envio", "EFY Finance", "RIF", "Efinti", "FastBTC", "PowPeg",
    "Token Bridge", "WayniMovil", "Enkrypt", "MyCrypto", "Ledger", "Trezor", "Edge Wallet", "Wallby", "SafePal", "Third Web", "Tacen",
    "Unitap", "SatoshiSync", "Etherspot", "Vottun", "The Graph", "Rootstock Explorer", "BlockScout", "Footprint Analytics", "Covalent",
     "Outlier Ventures", "Luxor", "Marathon", "Colledge", "UniFi", "Think & Dev", "WakeUp Labs", "Infuy"];

  const [squery, setSquery] = useState("");
  const [mainCat, setMainCat] = useState(0);
  const [subCat, setSubCat] = useState(0);

  const sortedCategories = specialCategories.map(name => categories.find(cat => cat.name.toLowerCase() === name)).filter(Boolean).concat(categories.filter(cat => !specialCategories.includes(cat.name.toLowerCase()) && cat.name.toLowerCase() !== 'others').sort((a, b) => a.name.localeCompare(b.name))).concat(categories.filter(cat => cat.name.toLowerCase() === 'others'));
  const categoriesTree = flatListToHierarchical(sortedCategories);

  // Sorting partners
  let specialPartnersList = [];
  let otherPartnersList = [];
  partners.forEach(partner => {
    if (specialPartners.includes(partner.node.title)) {
      specialPartnersList.push(partner);
    } else {
      otherPartnersList.push(partner);
    }
  });

  // Combine special partners and others, preserving order of special partners
  let sortedPartners = specialPartners.map(name => specialPartnersList.find(partner => partner.node.title === name)).filter(Boolean).concat(otherPartnersList.sort((a, b) => a.node.title.localeCompare(b.node.title)));
  const [items] = useState(sortedPartners);

  const pageSize = 16;
  const [currentPage, setPage] = useState(1);
  const siblingCount = 0;
  const totalCount = search(items).length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginationRange = usePagination({ currentPage, totalCount, siblingCount, pageSize });

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const hashParts = hash.startsWith('#') ? hash.slice(1).split('&') : hash.split('&'); // Handle both with and without #
      const mainAnchor = hashParts[0] === 'ecosystem' ? 'ecosystem' : null; // Check if #ecosystem is present
      const params = new URLSearchParams(hashParts.slice(1).join('&'));

      const categoryParam = params.get('category');
      const subcategoryParam = params.get('subcategory');

      if (categoryParam) {
        const category = categories.find(cat => cat.name.toLowerCase() === categoryParam.toLowerCase());
        if (category) {
          setMainCat(category.id);
        }
      }

      if (subcategoryParam && mainCat) {
        const subcategory = categoriesTree
          .find(cat => cat.id === mainCat)
          ?.children?.find(sub => sub.name.toLowerCase() === subcategoryParam.toLowerCase());
        if (subcategory) {
          setSubCat(subcategory.id);
        }
      }

      // Scroll to the anchor section if #ecosystem is explicitly present in the URL
      if (mainAnchor === 'ecosystem') {
        const ecosystemSection = document.getElementById('ecosystem');
        if (ecosystemSection) {
          ecosystemSection.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [categories, mainCat, categoriesTree]);

  const updateHash = (category, subcategory = null) => {
    // Construct the new hash without forcing the #ecosystem anchor
    let newHash = `category=${encodeURIComponent(category)}`;
    if (subcategory) {
      newHash += `&subcategory=${encodeURIComponent(subcategory)}`;
    }
    window.location.hash = newHash;
  };

  function goSearch(event, query) {
    event.preventDefault();
    setPage(1);
    setSquery(query);
  }

  function search(items) {
    return items.filter(
      (item) =>
        (
          (!mainCat && !subCat) || (
            item.node?.category?.nodes.length > 0 && item.node.category.nodes.some(cat => (cat.id === +mainCat)) &&
            (subCat === 0 || item.node.category.nodes.some(cat => (cat.id === +subCat)))
          )
        )
        &&
        (item.node.title.toString().toLowerCase().indexOf(squery.toLowerCase()) > -1)
    );
  }

  return (
    <section id='ecosystem' className="pt-lg-16 pb-lg-11 pt-md-11 pb-md-13 pt-9 pb-11">
      <div className="container">
        <div className="row mb-7 mb-md-8 mb-lg-11">
          <div className="col-12">
            <span className="zg-label bg-pink me-2 mb-4 mb-lg-6">{fields.number}</span><span className="zg-label bg-pink mb-4 mb-lg-6">{fields.suptitle}</span>
          </div>
          <div className="col-md-7 col-12 col-lg-6">
            <h2 className="mb-6 mb-md-0 h3" dangerouslySetInnerHTML={{ __html: fields.title }}></h2>
          </div>
          <div className="col-md-5 col-lg-3 mb-4 mb-md-6 mb-lg-0">
            {fields.text && (
              <div className="zg-text-sm ps-xl-8 zg-text-block" dangerouslySetInnerHTML={{ __html: fields.text }}></div>
            )}
          </div>
          <div className="col-lg-3 col-md-5 offset-md-7 offset-lg-0 text-lg-end">
            {link.title && (
              <Button
                title="Submit Project"
                url="https://form.typeform.com/to/cVsk5yfF"
                classes="btn-lg"
                external="true"
              />
            )}
          </div>
        </div>

        <div className="row mb-6 mb-lg-8">
          <div className="col-12 col-md-12 col-lg">
            <div className={'d-flex flex-wrap gap-3'}>
              <button
                onClick={() => {
                  setMainCat(0);
                  setPage(1);
                  setSubCat(0);
                  updateHash("");
                }}
                className={`btn-alt btn-alt--md ${mainCat === 0 ? 'active' : ''}`}><span>{t("All")}</span></button>
              {categoriesTree.map((item, index) => (
                <button
                  onClick={() => {
                    setMainCat(item.id);
                    setSubCat(0);
                    setPage(1);
                    updateHash(item.name); // Only update category parameter, do not add #ecosystem
                  }}
                  className={`btn-alt btn-alt--md ${mainCat === item.id ? 'active' : ''}`} key={index}><span>{item.name}</span></button>
              ))}
            </div>
            {categoriesTree.map((item, index) => (mainCat === item.id && item.children?.length > 0) && (
              <div className={`d-flex flex-wrap gap-3 mt-4`} key={index}>
                {item.children && item.children.map((child, indexChild) => (
                  <button key={indexChild}
                    onClick={() => {
                      setSubCat(child.id);
                      setPage(1);
                      updateHash(item.name, child.name); // Only update category and subcategory parameters, do not add #ecosystem
                    }}
                    className={`btn btn-outline-white btn-outline-white--xs ${subCat === child.id ? 'active' : ''}`}><span>{child.name}</span></button>
                ))}
              </div>
            ))}
          </div>
          <div className="col-12 col-lg-4 col-xl-3">
            <SearchForm onSend={goSearch} cssClass="w-100 mt-4 mt-lg-0 mt-lg-n2" placeholder={t("Search projects")} />
          </div>
        </div>

        <div className="zg-ecosystem-grid">
          {
            search(items)
              .slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)
              .map((item, index) => (
                <CardPartner
                  query={squery}
                  key={index}
                  data={item.node}
                  title={item.node.title}
                  image={item.node?.image?.node?.localFile?.publicURL}
                  link={item.node?.acf?.link}
                />
              ))
          }
        </div>
        {(search(items).length === 0 && (
          <div className="h5 mb-0">{t(`Sorry, we couldn’t find any results`)}</div>
        ))}
        {totalPages * pageSize > pageSize && (
          <div className="row">
            <div className="col-12 col-xl-6 offset-xl-6">
              <div className="d-flex justify-content-between mt-4 mt-md-7 mt-lg-6">
                <button onClick={(e) => setPage(currentPage - 1)} className="btn-alt btn-page-number"
                  disabled={currentPage === 1}>
                  <span className="px-md-4">
                    <svg width="26" height="24" viewBox="0 0 26 24" fill="none">
                      <path d="M25 12L1 12" stroke="#FAF9F5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M8 19L1 12L8 5" stroke="#FAF9F5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                <div className="d-flex mx-1 mx-md-2 mx-lg-4">
                  {paginationRange.map((pageNumber, index) => {
                    if (pageNumber === DOTS) {
                      return <span className={`btn-alt btn-page-number mx-1`} key={index}><span>...</span></span>;
                    }

                    return (
                      <button onClick={(e) => setPage(pageNumber)}
                        className={`btn-alt btn-page-number mx-1 ${pageNumber === currentPage ? "active" : ""}`}
                        key={index}><span>{pageNumber}</span></button>
                    );
                  })}
                </div>
                <button onClick={(e) => setPage(currentPage + 1)} className="btn-alt btn-page-number"
                  disabled={currentPage === totalPages}>
                  <span className="px-md-4">
                    <svg width="26" height="24" viewBox="0 0 26 24" fill="none">
                      <path d="M1 12L25 12" stroke="#FAF9F5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18 5L25 12L18 19" stroke="#FAF9F5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
